import { useEffect, useMemo, useState } from 'react';

const DEFAULT_STYLE = {
  backgroundColor: '#0f172a',
  textColor: '#f8fafc',
  accentColor: '#ef4444',
  labelColor: '#e2e8f0',
  digitBackground: 'rgba(15, 23, 42, 0.92)',
  digitTextColor: '#f8fafc',
  badgeBackground: '#1e293b',
  badgeTextColor: '#f8fafc',
  badgeText: '🔥 Limited seats available',
  fontFamily: 'Poppins, sans-serif',
  borderRadius: '1rem',
  uppercaseLabels: true,
  showShadow: true,
  layout: 'stacked',
  expireMessage: 'The countdown has ended — contact us to reserve your spot!'
};

export const COUNTDOWN_DEFAULT_STYLE = Object.freeze({ ...DEFAULT_STYLE });

const MS_IN_SECOND = 1000;
const MS_IN_MINUTE = 60 * MS_IN_SECOND;
const MS_IN_HOUR = 60 * MS_IN_MINUTE;
const MS_IN_DAY = 24 * MS_IN_HOUR;

function parseTargetDate(target) {
  if (!target) return null;
  if (target instanceof Date && !Number.isNaN(target.valueOf())) return target;
  const parsed = new Date(target);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
}

function pad(value) {
  return String(Math.max(0, value)).padStart(2, '0');
}

function buildUnits(diff) {
  const days = Math.floor(diff / MS_IN_DAY);
  const hours = Math.floor((diff % MS_IN_DAY) / MS_IN_HOUR);
  const minutes = Math.floor((diff % MS_IN_HOUR) / MS_IN_MINUTE);
  const seconds = Math.floor((diff % MS_IN_MINUTE) / MS_IN_SECOND);

  return [
    { key: 'days', label: 'Days', value: pad(days) },
    { key: 'hours', label: 'Hours', value: pad(hours) },
    { key: 'minutes', label: 'Minutes', value: pad(minutes) },
    { key: 'seconds', label: 'Seconds', value: pad(seconds) }
  ];
}

export default function CountdownTimer({ targetDate, text, style = {}, onExpireAction = 'hide', className = '' }) {
  const resolvedStyle = useMemo(() => ({ ...DEFAULT_STYLE, ...(style || {}) }), [style]);
  const parsedTarget = useMemo(() => parseTargetDate(targetDate), [targetDate]);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!parsedTarget) return undefined;

    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [parsedTarget]);

  const diff = useMemo(() => {
    if (!parsedTarget) return null;
    return parsedTarget.getTime() - now.getTime();
  }, [parsedTarget, now]);

  if (!parsedTarget || diff === null) {
    return null;
  }

  const expired = diff <= 0;
  const expireMode = typeof onExpireAction === 'string' ? onExpireAction.toLowerCase() : 'hide';

  if (expired && expireMode === 'hide') {
    return null;
  }

  const containerStyle = {
    backgroundColor: resolvedStyle.backgroundColor,
    color: resolvedStyle.textColor,
    borderRadius: resolvedStyle.borderRadius,
    boxShadow: resolvedStyle.showShadow ? '0 25px 60px -20px rgba(15, 23, 42, 0.65)' : 'none',
    fontFamily: resolvedStyle.fontFamily,
  };
  const baseOuter = 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8';
  const outerClassName = className ? `${baseOuter} ${className}` : baseOuter;

  const badgeStyles = {
    backgroundColor: resolvedStyle.badgeBackground,
    color: resolvedStyle.badgeTextColor,
  };

  const unitStyles = {
    backgroundColor: resolvedStyle.digitBackground,
    color: resolvedStyle.digitTextColor,
  };

  if (expired && expireMode === 'message') {
    return (
      <div className={outerClassName}>
        <div className="relative overflow-hidden border border-white/10 px-6 py-7 sm:px-10" style={containerStyle}>
          <span
            className="absolute inset-x-0 top-0 h-1"
            style={{ background: `linear-gradient(90deg, ${resolvedStyle.accentColor}, transparent)` }}
            aria-hidden="true"
          />
          <div className="flex flex-col items-center gap-4 sm:gap-6 text-center">
            <span className="inline-flex items-center rounded-full px-3 py-1 text-sm sm:text-base font-semibold tracking-wide" style={badgeStyles}>
              {resolvedStyle.badgeText}
            </span>
            <p className="text-xl sm:text-2xl font-semibold" style={{ color: resolvedStyle.textColor }}>
              {resolvedStyle.expireMessage || DEFAULT_STYLE.expireMessage}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const units = buildUnits(diff);
  const labelTransform = resolvedStyle.uppercaseLabels ? 'uppercase' : undefined;
  const layoutStacked = resolvedStyle.layout === 'stacked';

  return (
    <div className={outerClassName}>
      <div className="relative overflow-hidden border border-white/10 px-6 py-7 sm:px-10" style={containerStyle}>
        <span
          className="absolute inset-x-0 top-0 h-1"
          style={{ background: `linear-gradient(90deg, ${resolvedStyle.accentColor}, transparent)` }}
          aria-hidden="true"
        />
        <div className={layoutStacked ? 'flex flex-col gap-6 md:flex-row md:items-center md:justify-between' : 'flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'}>
          <div className="space-y-3 text-center md:text-left">
            <span className="inline-flex items-center rounded-full px-3 py-1 text-sm sm:text-base font-semibold tracking-wide" style={badgeStyles}>
              {resolvedStyle.badgeText}
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: resolvedStyle.textColor }}>
              {text}
            </h2>
          </div>
          <div
            className={layoutStacked
              ? 'grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5'
              : 'flex flex-wrap items-center justify-center gap-4 sm:gap-6'}
          >
            {units.map((unit) => (
              <div
                key={unit.key}
                className="flex min-w-[5.5rem] flex-col items-center rounded-2xl px-4 py-3 text-center shadow-sm"
                style={unitStyles}
              >
                <span className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                  {unit.value}
                </span>
                <span
                  className="mt-1 text-xs sm:text-sm font-semibold tracking-wide text-slate-300"
                  style={{ color: resolvedStyle.labelColor, textTransform: labelTransform }}
                >
                  {unit.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
